import { Stack, Tag } from '@chakra-ui/react';
import { Vehicle, Workshop } from 'api/types';
import Link from 'components/Link';
import useSystemPartnerQuery from 'hooks/useSystemPartnerQuery';
import { get, isEqual, isNil, keys } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import { MultiSelectDropDown } from '../../components/Table/MultiSelectDropDown';
import { useUserContext } from '../../context/UserContextProvider';
import { dateTimeTransformer, dateTimezoneTransformer } from '../../helpers/dateTransformer';
import { createDropdownPlainOptions, createDropdownTranslatedStaticOptions } from '../../helpers/general';
import useServiceRecordsQuery from '../../hooks/useServiceRecordsQuery';
import { ContentCard } from '../../layout/ContentCard';

const ServiceRecordOverviewCard = () => {
  const { t } = useTranslation();
  const { statusCodes, makes, config } = useUserContext();
  const systemPartenerQuery = useSystemPartnerQuery();
  const systemPartnersData = systemPartenerQuery?.data || [];
  const systemPartners = [
    { name: getI18n().t('workshop:system_partner.no_system_partner'), id: 'none' },
    { name: getI18n().t('workshop:system_partner.with_system_partner'), id: 'all' },
    ...systemPartnersData,
  ];

  const serviceRecordStatusCodes = get(statusCodes, 'service_record', {});
  const initialStatus = keys(serviceRecordStatusCodes).filter((code) => !['0'].includes(code));
  const languagesWithDuplicates = Object.values(config?.languages).flat();
  const dropdownLanguages = [...new Set(languagesWithDuplicates)];

  const dropdownCountries = Object.values(config?.countries);

  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: initialStatus || [],
    assignee: undefined,
    make: undefined,
    fastlane: undefined,
    language: undefined,
    systemPartner: undefined,
    country: undefined,
  };
  const localStorageData = window.localStorage.getItem('service_records_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('service_records_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [make, setMake] = useState(destringifiedData?.make || initialFilters?.make);
  const [language, setLanguage] = useState(destringifiedData?.language || initialFilters?.language);
  const [assignee, setAssignee] = useState(destringifiedData?.assignee || initialFilters?.assignee);
  const [systemPartner, setSystemPartner] = useState(destringifiedData?.systemPartner || initialFilters?.systemPartner);
  const [fastlane, setFastlane] = useState(destringifiedData?.fastlane || initialFilters?.fastlane);

  const [country, setCountry] = useState(destringifiedData?.country || initialFilters?.country);

  const [filtersSelected, setFiltersSelected] = useState(false);
  const serviceRecordsQuery = useServiceRecordsQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    make,
    language,
    assignee,
    fastlane,
    systemPartner,
    country,
  });

  // @ts-ignore
  const currentPage = serviceRecordsQuery.data?.meta?.current_page - 1;
  const initialLoad =
    searchString === '' &&
    itemsPerPage === 20 &&
    page === 1 &&
    isEqual(initialStatus, status) &&
    isNil(assignee) &&
    isNil(make) &&
    isNil(language) &&
    isNil(fastlane) &&
    isNil(country);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, status, make, country]);
  const allStatusSelected = status.length === Object.keys(serviceRecordStatusCodes).length;

  useEffect(() => {
    setFiltersSelected(!isNil(assignee) || !isNil(fastlane) || !isNil(make) || !isNil(language) || !isNil(country) || status?.length !== 0);

    window.localStorage.setItem(
      'service_records_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        assignee: assignee,
        make: make,
        language: language,
        fastlane: fastlane,
        itemsPerPage: itemsPerPage,
        systemPartner: systemPartner,
        country: country,
      }),
    );
  }, [searchString, status, assignee, make, language, systemPartner, fastlane, itemsPerPage, country]);

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'user_readable_id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link data-test-id={'sr-' + row?.index} to={`/app/op/service-record/${row.original.id}`}>
              {row.original.user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('forms:open_task.label'),
        accessor: 'open_task.last_checked_at',
        // @ts-ignore
        Cell: ({ value }) => {
          return (
            <Tag variant={'solid'} size={'md'} padding={'6px'} colorScheme={isNil(value) ? 'red' : 'green'}>
              {isNil(value) ? t('tasks:last_checked_at.not_checked') : dateTimezoneTransformer(value, 'Europe/Vienna', true)}
            </Tag>
          );
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () => MultiSelectDropDown(status, setStatus, 'service_record', serviceRecordStatusCodes, allStatusSelected, t('forms:status.label')),

        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'service_record'} currentStatus={value} />,
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (props) => {
          return props.value ?? '-';
        },
      },
      {
        Header: t('forms:submitted_at.label', { date: '' }),
        accessor: (row: { submitted_at: string }) => dateTimeTransformer(row.submitted_at) ?? '-',
      },
      {
        Header: t('forms:customer.label'),
        accessor: (row: { workshop: Workshop }) => <Link to={`/app/op/workshops/${row.workshop?.id}`}>{row.workshop?.user_readable_id}</Link>,
      },
      {
        Header: t('workshop:language.label'),
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: language,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: dropdownLanguages,
              translationKey: 'location:languages',
            }),
            onChange: setLanguage,
          }),
        accessor: (row: { workshop: Workshop }) => t(`location:languages.${row.workshop?.language.toUpperCase()}`),
      },
      {
        Header: t('forms:fastlane.label'),
        accessor: 'fastlane',
        // @ts-ignore
        Cell: ({ value }) => (value === 1 ? t('common:yes') : t('common:no')),
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: fastlane,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: [
                { label: t('forms:fastlane.non_fastlane'), value: 0 },
                { label: t('forms:fastlane.label'), value: 1 },
              ],
              valueColumn: 'value',
              labelColumn: 'label',
            }),
            onChange: setFastlane,
          }),
      },
      {
        Header: t('forms:vin.label'),
        accessor: (row: { vehicle: Vehicle }) => {
          return row.vehicle?.vin;
        },
      },
      {
        Header: t('forms:make.label'),
        accessor: (row: { vehicle: Vehicle }) => row.vehicle?.manufacturer?.make?.name,
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: make,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: makes,
              labelColumn: 'name',
              valueColumn: 'id',
            }),
            onChange: setMake,
          }),
      },
      {
        Header: t('workshop:system_partner.label'),
        accessor: 'systempartner_id',
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: systemPartner,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: systemPartners as any,
              valueColumn: 'id',
              labelColumn: 'name',
            }),
            onChange: setSystemPartner,
          }),
        // @ts-ignore
      },
      {
        Header: t('workshop:country.label'),
        accessor: (row: { workshop: Workshop }) => t(`location:countries.${row?.workshop?.country}`),
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: country,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: dropdownCountries,
              translationKey: 'location:countries',
            }),
            onChange: setCountry,
          }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      serviceRecordStatusCodes,
      searchString,
      status,
      assignee,
      make,
      language,
      fastlane,
      serviceRecordsQuery.data,
      systemPartner,
      systemPartners,
      country,
    ],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('service_record:header')}
      data-test-id="service-records-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setAssignee(initialFilters?.assignee);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setMake(initialFilters?.make);
              setLanguage(initialFilters?.language);
              setFastlane(initialFilters?.fastlane);
              setSystemPartner(initialFilters?.systemPartner);
              setCountry(initialFilters?.country);
              window.localStorage.setItem('service_records_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="service-records-table"
            data={serviceRecordsQuery.data?.data || []}
            globalFilter={searchString}
            filtersSelected={filtersSelected}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('service_record:search', 'Name, Email, Kunden Id ...')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            total={serviceRecordsQuery.data?.meta?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={serviceRecordsQuery.isFetching || serviceRecordsQuery.isLoading}
            isMoreThenOnePage={!initialLoad || Boolean(serviceRecordsQuery.data?.links?.next) || currentPage > 0}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default ServiceRecordOverviewCard;
